/**
 * Loads the background-music assets the {@link ./musicPlayer.MusicPlayer} streams
 * through the in-browser synth (ADR-0146): the baked UO `.mid` tracks and the bundled
 * General-MIDI soundfont. The audio analogue of {@link ./soundAssets}: the `.mid` set is
 * discovered with `import.meta.glob` over `assets/baked/music/`, which the stub (or the
 * real converter) fills by walking the SAME {@link ../sim/content.music} registry, so
 * what is fetched here can never drift from what is baked.
 *
 * The `.mid` files are gitignored copyrighted UO audio; a clientless host has short
 * silent placeholders (`tools/stub-assets.ts`), so this resolves to silent tracks rather
 * than failing - the pipeline stays wired. The **soundfont**, by contrast, is freely
 * redistributable (GeneralUser GS v2.0, permissive) and therefore a **committed** asset
 * under `assets/soundfont/`, statically imported so it is always present. A fetch/decode
 * failure anywhere degrades to "no music" without throwing, exactly as a missing wav
 * degrades to silence in the sound path.
 */
import { music as musicDefs, arenaRemixDefs } from '../sim/content';
// The committed General-MIDI soundfont (GeneralUser GS v2.0, permissive license -
// see assets/soundfont/GeneralUser-GS-LICENSE.txt). Static `?url` import so Vite
// hashes and serves it as a fetchable asset (never inlined into the JS bundle).
import soundfontUrl from '../../assets/soundfont/GeneralUserGS.sf3?url';

// The baked `.mid` urls, discovered the same way the sound wavs are - glob'd because
// the set is registry-driven. Vite resolves these at build/test time against
// `assets/baked/music/`, keyed by relative path; re-keyed to `<id>` below.
const midiUrls = import.meta.glob('../../assets/baked/music/*.mid', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

// The baked Arena intensity-remix `.mp3`s: pre-rendered audio played directly (never
// through the synth), globbed beside the mids. The stub (or the real converter) fills
// these by walking the same derived {@link ../sim/content.arenaRemixDefs} set, so the
// loaded remixes can never drift from what is baked.
const remixUrls = import.meta.glob('../../assets/baked/music/*.mp3', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

/** Re-key a glob result by its filename without the extension (the track `<id>`). */
function byId<T>(glob: Record<string, T>): Map<string, T> {
  const out = new Map<string, T>();
  for (const [path, value] of Object.entries(glob)) {
    const file = path.slice(path.lastIndexOf('/') + 1).replace(/\.(mid|mp3)$/, '');
    out.set(file, value);
  }
  return out;
}

/** The URL Vite serves the bundled soundfont at. */
export const SOUNDFONT_URL: string = soundfontUrl;

/** One loaded MIDI track: its playlist id and the raw Standard-MIDI-File bytes. */
export interface LoadedTrack {
  id: string;
  binary: ArrayBuffer;
}

/**
 * Fetch the bundled soundfont's bytes for the synth's sound-bank manager. Returns
 * `undefined` (with a warning) rather than throwing if the fetch fails, so the music
 * player can quietly disable itself instead of crashing login.
 */
export async function loadSoundfont(): Promise<ArrayBuffer | undefined> {
  try {
    return await fetch(SOUNDFONT_URL).then((r) => r.arrayBuffer());
  } catch (err) {
    console.warn('failed to load soundfont - music disabled:', err);
    return undefined;
  }
}

/**
 * Fetch every registered music track's baked `.mid` bytes, keyed by content id, in the
 * {@link ../sim/content.music} registry's declared order. Walks the same registry the
 * converter/stub bake from, so every registered id is fetched; a track whose `.mid` is
 * missing or fails to fetch is skipped with a warning rather than aborting the others,
 * so one bad file never kills the whole playlist.
 */
export async function loadMusicTracks(): Promise<LoadedTrack[]> {
  const urls = byId(midiUrls);
  const loaded = await Promise.all(
    musicDefs.all().map(async (def): Promise<LoadedTrack | undefined> => {
      const url = urls.get(def.id);
      if (!url) {
        console.warn(`no baked mid for music "${def.id}" - skipped`);
        return undefined;
      }
      try {
        const binary = await fetch(url).then((r) => r.arrayBuffer());
        return { id: def.id, binary };
      } catch (err) {
        console.warn(`failed to load music "${def.id}":`, err);
        return undefined;
      }
    }),
  );
  return loaded.filter((t): t is LoadedTrack => t !== undefined);
}

/**
 * Fetch Arena intensity remixes' baked `.mp3` bytes, keyed by remix id
 * (`<track>_<tier>`), walking the same derived {@link ../sim/content.arenaRemixDefs}
 * set the converter/stub bake from. The bytes are raw encoded audio for
 * `AudioContext.decodeAudioData` (the player decodes; this only fetches). A remix
 * whose file is missing or fails to fetch is skipped with a warning - the
 * MusicDirector degrades a missing tier down toward the base `.mid`, so a partial
 * bake never silences a run.
 *
 * Pass `ids` to fetch **only** those remix ids (the current run song's `_2.._5`); this is
 * the deferred, per-run load the {@link ../audio/musicPlayer.MusicPlayer} uses so the
 * ~85MB mp3 lane is never downloaded until a player actually enters the Arena, and only
 * the one chosen song's remixes are fetched. Omit `ids` to fetch the whole set.
 */
export async function loadMusicRemixes(ids?: readonly string[]): Promise<LoadedTrack[]> {
  const urls = byId(remixUrls);
  const want = ids ? new Set(ids) : null;
  const defs = want ? arenaRemixDefs().filter((d) => want.has(d.id)) : arenaRemixDefs();
  const loaded = await Promise.all(
    defs.map(async (def): Promise<LoadedTrack | undefined> => {
      const url = urls.get(def.id);
      if (!url) {
        console.warn(`no baked mp3 for arena remix "${def.id}" - skipped`);
        return undefined;
      }
      try {
        const binary = await fetch(url).then((r) => r.arrayBuffer());
        return { id: def.id, binary };
      } catch (err) {
        console.warn(`failed to load arena remix "${def.id}":`, err);
        return undefined;
      }
    }),
  );
  return loaded.filter((t): t is LoadedTrack => t !== undefined);
}
